import {
	Box,
	Button,
	IconButton,
	MenuItem,
	Select,
	Typography,
	useTheme,
} from '@mui/material';
import React, { Dispatch, SetStateAction, useContext } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Block, Edge } from 'src/types/shared/blocksV3';
import {
	BumpoutEdgeTypes,
	DeckBoxEdgeTypes,
	DeckContext,
	LandingEdgeTypes,
	StairEdgeTypes,
} from '../../../../context/DeckContextV3';
import FeetInchesInput from '../../../FeetInchesInputV2';
import { v4 as uuidv4 } from 'uuid';
import EdgeType from 'src/enums/edgeType';
import BlockType from 'src/enums/blockType';
import { toastError } from 'src/Toaster/Toast';

const SegmentOptions: React.FC<{
	setStage: Dispatch<SetStateAction<number>>;
	handleCloseDialog: () => void;
	block: Block | null;
	edge: Edge | undefined;
	setIsDropdownOpen: Dispatch<SetStateAction<boolean>>;
	segmentLength: number;
	setSegmentLength: Dispatch<SetStateAction<number>>;
	segmentType: string;
	setSegmentType: Dispatch<SetStateAction<string>>;
}> = ({
	setStage,
	handleCloseDialog,
	block,
	edge,
	setIsDropdownOpen,
	segmentLength,
	setSegmentLength,
	segmentType,
	setSegmentType,
}) => {
	const { dispatch } = useContext(DeckContext);

	const theme = useTheme();

	const handleAddSegment = () => {
		const parentEdgeLengthAfterUpdate =
			(edge?.segments.reduce((acc, seg) => {
				acc += seg.length;

				return acc;
			}, 0) || 0) + segmentLength;

		if ((parentEdgeLengthAfterUpdate || 0) > (edge?.length || 0)) {
			toastError({
				title: 'Length',
				description: `Total segments length exceeds edge length (${(edge?.length || 0) / 12} ft) `,
			});

			return;
		}

		if (block && edge && segmentLength && segmentType && segmentType !== '') {
			const updatedBlock = {
				...block,
				edges: block.edges.map((ed) => {
					if (ed.id == edge.id) {
						const updatedEdge = { ...ed };

						updatedEdge.segments = [
							...updatedEdge.segments,
							{
								id: uuidv4(),
								edgeId: edge.id,
								blockId: block.id || '',
								length: segmentLength,
								type: segmentType as EdgeType,
							},
						];

						return updatedEdge;
					} else {
						return ed;
					}
				}),
			};

			dispatch({ type: 'UPDATE_BLOCK', payload: updatedBlock });
			setStage(0);
		}
		// setStage(0);
	};

	let edgeTypes: EdgeType[] = [];

	switch (block?.type) {
		case BlockType.DECKBOX:
			edgeTypes = DeckBoxEdgeTypes;
			break;
		case BlockType.BUMPOUT:
			edgeTypes = BumpoutEdgeTypes;
			break;
		case BlockType.STAIR:
			edgeTypes = StairEdgeTypes;
			break;
		case BlockType.LANDING:
			edgeTypes = LandingEdgeTypes;
			break;
		default:
			edgeTypes = [];
	}

	const isButtonDisabled = !segmentLength || !segmentType || segmentType === '';

	return (
		<Box sx={{ paddingY: '10px', width: '100%' }}>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '10px',
					paddingRight: '10px',
				}}
			>
				<Button
					startIcon={<ArrowBackIcon />}
					sx={{ color: 'black', fontWeight: 500 }}
					onClick={() => setStage(0)}
				>
					Back
				</Button>

				<IconButton onClick={handleCloseDialog}>
					<CloseIcon sx={{ color: theme.palette.grey[500] }} />
				</IconButton>
			</Box>

			<Box sx={{ padding: '10px' }}>
				<FeetInchesInput
					valueInches={segmentLength}
					labelInches="Length"
					onChange={(inches) => setSegmentLength(inches)}
				/>

				<Typography id="edge-select-label" sx={{ marginTop: '15px' }}>
					Edge Type
				</Typography>
				<Select
					labelId="edge-select-label"
					variant="outlined"
					size="small"
					fullWidth
					value={segmentType}
					sx={{ marginY: '5px' }}
					onChange={(e) => setSegmentType(e.target.value as string)}
					onOpen={() => setIsDropdownOpen(true)}
					onClose={() => setIsDropdownOpen(false)}
					displayEmpty
				>
					<MenuItem value="" disabled>
						Select Edge Type
					</MenuItem>
					{edgeTypes.map((edge, index) => (
						<MenuItem key={index} value={edge}>
							{edge}
						</MenuItem>
					))}
				</Select>
			</Box>

			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-around',
					marginTop: '20px',
					boxShadow: '0 0 6px 0 rgba(158, 163, 170, 0.37)',
					padding: '10px',
					flex: '0 0 auto',
				}}
			>
				<Button
					onClick={() => setStage(0)}
					variant="contained"
					sx={{
						textTransform: 'none',
						backgroundColor: '#E3FBE3',
						boxShadow: '0px 0px',
						color: 'black',
						'&:hover': {
							color: theme.palette.common.white,
							backgroundColor: 'green',
						},
					}}
				>
					Cancel
				</Button>

				<Button
					onClick={handleAddSegment}
					variant="contained"
					sx={{
						textTransform: 'none',
						color: 'black',
						backgroundColor: '#E3FBE3',
						'&:hover': {
							color: theme.palette.common.white,
							backgroundColor: 'green',
						},
					}}
					disabled={isButtonDisabled}
				>
					Add
				</Button>
			</Box>
		</Box>
	);
};

export default SegmentOptions;
